import { useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { locale } from "@/lib/format";
import { api } from "@/lib/api";
import { Calendar, Users, Receipt, TrendingUp, Sparkles, PieChart, ArrowRightLeft, ArrowUpRight, ArrowDownRight, Ticket, Scissors, Timer, Layers, Clock, CheckCircle2, CalendarRange, Gift, LayoutDashboard, AlertTriangle, Package, Target, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { DonutChart } from "@/components/DonutChart";

const MONTHS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const CAT_LABEL = { rent: "dashboard.cat.rent", utilities: "dashboard.cat.utilities", products: "dashboard.cat.products", staff: "dashboard.cat.staff", marketing: "dashboard.cat.marketing", taxes: "dashboard.cat.taxes", other: "dashboard.cat.other", utenze: "dashboard.cat.utilities", attrezzatura: "dashboard.cat.equipment", personale: "dashboard.cat.staff", fornitori: "dashboard.cat.suppliers", fiscalita: "dashboard.cat.tax" };
// Palette allineata a DEFAULT_PALETTE (Tailwind 400) usata in tutta l'app
const CAT_COLOR = {
  utenze: "#FBBF24", utilities: "#FBBF24", rent: "#FBBF24",
  attrezzatura: "#22D3EE", products: "#22D3EE",
  personale: "#4ADE80", staff: "#4ADE80",
  fornitori: "#A78BFA", marketing: "#A78BFA",
  fiscalita: "#F87171", taxes: "#F87171",
  other: "#94A3B8",
};

function ymd(d) { return d.toISOString().slice(0, 10); }
function fmtItDate(iso) {
  if (!iso || typeof iso !== "string" || iso.length < 10) return iso || "";
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}
function firstOfMonth(dateStr) { const d = new Date(dateStr + "T00:00:00Z"); return ymd(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))); }
function lastOfMonth(dateStr) { const d = new Date(dateStr + "T00:00:00Z"); return ymd(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))); }
function sameMonthLastYear(dateStr) { const d = new Date(dateStr + "T00:00:00Z"); d.setUTCFullYear(d.getUTCFullYear() - 1); return ymd(d); }

const Stat = ({ icon: Icon, label, value, sub, tone = "default", testId }) => (
  <div className="sp-card p-6 sp-anim" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="label-eyebrow">{label}</span>
      {Icon && <Icon className={`h-4 w-4 ${tone === "brand" ? "text-[color:var(--sp-brand)]" : ""}`} />}
    </div>
    <div className={`font-display text-3xl font-black mt-3 ${tone === "brand" ? "text-[color:var(--sp-brand)]" : ""}`}>{value}</div>
    {sub && <div className="text-xs text-[color:var(--sp-text-secondary)] mt-1">{sub}</div>}
  </div>
);

const eur = (n) => `€ ${(n ?? 0).toLocaleString(locale(), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const eur2 = (n) => `€ ${(n ?? 0).toLocaleString(locale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Global range presets
const PRESETS = [
  { key: "month", label: "dashboard.presets.month", compute: (t) => ({ from: firstOfMonth(t), to: lastOfMonth(t) }) },
  { key: "quarter", label: "dashboard.presets.quarter", compute: (t) => { const d = new Date(t + "T00:00:00Z"); d.setUTCMonth(d.getUTCMonth() - 2); return { from: firstOfMonth(ymd(d)), to: lastOfMonth(t) }; } },
  { key: "ytd", label: "dashboard.presets.ytd", compute: (t) => ({ from: `${t.slice(0, 4)}-01-01`, to: t }) },
  { key: "custom", label: "dashboard.presets.custom", compute: null },
];

const TREND_OPTIONS = [
  { key: "3", label: "3M", months: 3 },
  { key: "6", label: "6M", months: 6 },
  { key: "12", label: "12M", months: 12 },
  { key: "max", label: "MAX", months: 999 },
];

export default function Dashboard() {
  const { t } = useTranslation();
  const [tenant, setTenant] = useState(null);
  const today = ymd(new Date());

  // Global range filter (drives KPI, metrics, expected, cost pie)
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(() => PRESETS[0].compute(today));

  const [kpi, setKpi] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [expected, setExpected] = useState(null);
  const [costsSummary, setCostsSummary] = useState(null);
  const [topServices, setTopServices] = useState([]);
  const [packagesLow, setPackagesLow] = useState([]);
  const [breakEven, setBreakEven] = useState(null);

  const [trendKey, setTrendKey] = useState("6");
  const [trend, setTrend] = useState([]);
  const [showCosts, setShowCosts] = useState(false);

  const [periodA, setPeriodA] = useState({ from: firstOfMonth(today), to: lastOfMonth(today) });
  const [periodB, setPeriodB] = useState({ from: firstOfMonth(sameMonthLastYear(today)), to: lastOfMonth(sameMonthLastYear(today)) });
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [comparePreset, setComparePreset] = useState("yoy"); // yoy | mom | custom

  // one-time tenant
  useEffect(() => {
    api.get("/tenant").then((r) => setTenant(r.data)).catch(() => {});
    api.get("/dashboard/top-services?limit=5").then((r) => setTopServices(r.data)).catch(() => {});
    api.get("/dashboard/packages-low").then((r) => setPackagesLow(r.data || [])).catch(() => {});
  }, []);

  // Reload when the global range changes
  useEffect(() => {
    const qs = `date_from=${range.from}&date_to=${range.to}`;
    api.get(`/dashboard/kpi?${qs}`).then((r) => setKpi(r.data)).catch(() => {});
    api.get(`/dashboard/metrics?${qs}`).then((r) => setMetrics(r.data)).catch(() => {});
    api.get(`/dashboard/expected-revenue?date_from=${range.from}&date_to=${range.to}`).then((r) => setExpected(r.data)).catch(() => {});
    api.get(`/costs/summary?${qs}`).then((r) => setCostsSummary(r.data)).catch(() => {});
    api.get(`/invoices/kpis?${qs}`).then((r) => setBreakEven(r.data)).catch(() => setBreakEven(null));
  }, [range.from, range.to]);

  useEffect(() => {
    const opt = TREND_OPTIONS.find((x) => x.key === trendKey);
    api.get(`/dashboard/trend?months=${opt.months}`).then((r) => setTrend(r.data.trend || []));
  }, [trendKey]);

  const fetchPeriod = async (p) => (await api.get(`/dashboard/period?date_from=${p.from}&date_to=${p.to}`)).data;
  useEffect(() => { fetchPeriod(periodA).then(setDataA).catch(() => setDataA(null)); }, [periodA.from, periodA.to]); // eslint-disable-line
  useEffect(() => { fetchPeriod(periodB).then(setDataB).catch(() => setDataB(null)); }, [periodB.from, periodB.to]); // eslint-disable-line

  const rangeLabel = useMemo(() => {
    const p = PRESETS.find((x) => x.key === preset);
    if (preset === "custom") return t("dashboard.customRange", { from: range.from, to: range.to });
    return p?.label ? t(p.label) : "";
  }, [preset, range, t]);

  const setPresetAndRange = (key) => {
    setPreset(key);
    const p = PRESETS.find((x) => x.key === key);
    if (p?.compute) setRange(p.compute(today));
  };

  const maxTrend = useMemo(() => {
    const vals = trend.map((t) => showCosts ? Math.max(t.revenue || 0, t.costs || 0) : (t.revenue || 0));
    return Math.max(1, ...vals);
  }, [trend, showCosts]);
  const revenueSum = trend.reduce((a, t) => a + t.revenue, 0);
  const costsSum = trend.reduce((a, t) => a + (t.costs || 0), 0);
  const bestMonth = trend.reduce((best, t) => (t.revenue > (best?.revenue ?? -1) ? t : best), null);

  const costsPie = useMemo(() => {
    const by = costsSummary?.by_category || costsSummary?.by_category_month || {};
    return Object.entries(by).filter(([, v]) => v > 0).map(([k, v]) => ({ label: t(CAT_LABEL[k] || k), value: v, color: CAT_COLOR[k] || "#6B7280", key: k }));
  }, [costsSummary]);
  const costsTotal = costsPie.reduce((a, d) => a + d.value, 0);

  return (
    <div data-testid="salon-dashboard" className="space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-4xl md:text-5xl font-black tracking-tight inline-flex items-center gap-3">
            <LayoutDashboard className="h-7 w-7 sm:h-8 sm:w-8 text-[color:var(--sp-brand)]" />
            <span>Dashboard{tenant?.name ? <> · <span className="text-gradient">{tenant.name}</span></> : ""}</span>
          </h1>
          <p className="text-[color:var(--sp-text-secondary)] mt-2">{t("dashboard.subtitle")}</p>
        </div>
      </header>

      {/* Global range filter */}
      <div className="sp-card p-4 flex flex-wrap items-center gap-3" data-testid="dashboard-range-filter">
        <div className="inline-flex items-center gap-2 text-xs text-[color:var(--sp-text-muted)]">
          <CalendarRange className="h-3.5 w-3.5" /> <span className="font-bold uppercase tracking-widest">{t("dashboard.period")}</span>
        </div>
        <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1">
          {PRESETS.map((p) => (
            <button key={p.key} data-testid={`range-preset-${p.key}`} onClick={() => setPresetAndRange(p.key)}
                    className={`px-3 py-1 rounded-full text-xs font-bold ${preset === p.key ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
              {t(p.label)}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="inline-flex items-center gap-2">
            <input data-testid="range-from" type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="sp-input !py-1 text-xs w-auto" />
            <span className="text-xs">→</span>
            <input data-testid="range-to" type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="sp-input !py-1 text-xs w-auto" />
          </div>
        )}
        <div className="ml-auto text-xs text-[color:var(--sp-text-muted)]" data-testid="range-label">{rangeLabel} · <strong>{fmtItDate(range.from)}</strong> → <strong>{fmtItDate(range.to)}</strong></div>
      </div>

      {/* Primary KPI row */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        <Stat testId="kpi-clients" label={t("dashboard.kpi.clients")} value={kpi?.total_clients ?? "—"} sub={t("dashboard.kpi.clientsSub")} />
        <Stat testId="kpi-today" label={t("dashboard.kpi.today")} value={kpi?.today_appointments ?? "—"} sub={t("dashboard.kpi.todaySub")} />
        <Stat testId="kpi-completed" label={t("dashboard.kpi.completed")} value={kpi?.appuntamenti_effettuati ?? "—"} sub={t("dashboard.kpi.completedSub")} />
        <Stat testId="kpi-passaggi" label={t("dashboard.kpi.checkouts")} value={kpi?.passaggi_cassa ?? "—"} sub={t("dashboard.kpi.checkoutsSub")} />
        <Stat testId="kpi-incassato" label={t("dashboard.kpi.revenue")} tone="brand" value={eur(kpi?.incassato)} sub={t("dashboard.kpi.revenueSub")} />
      </section>

      {/* Business metrics */}
      <section data-testid="business-metrics" className="grid grid-cols-2 lg:grid-cols-5 gap-5">
        <Stat testId="metric-scontrino" label={t("dashboard.metrics.avgTicket")} value={metrics ? eur2(metrics.scontrino_medio) : "—"} sub={metrics ? t("dashboard.metrics.checkoutsCount", { n: metrics.passaggi_cassa }) : ""} />
        <Stat testId="metric-fiche" label={t("dashboard.metrics.avgService")} value={metrics ? eur2(metrics.fiche_media) : "—"} sub={metrics ? t("dashboard.metrics.avgServiceSub", { amount: eur(metrics.service_revenue), n: metrics.appuntamenti_effettuati }) : ""} />
        <Stat testId="metric-costmin" label={t("dashboard.metrics.costPerMinute")} value={metrics ? eur2(metrics.costo_al_minuto) : "—"} sub={metrics ? t("dashboard.metrics.openMinutes", { n: metrics.total_opening_minutes }) : ""} />
        <Stat testId="metric-svcpass" label={t("dashboard.metrics.servicesPerVisit")} value={metrics ? metrics.servizi_per_passaggio.toFixed(1) : "—"} sub={t("dashboard.metrics.servicesPerVisitSub")} />
        <Stat testId="metric-percorsi" tone="brand" label={t("dashboard.metrics.packagesSold")} value={kpi?.percorsi_venduti ?? "—"} sub={kpi ? t("dashboard.metrics.packagesSub", { amount: eur(kpi.percorsi_incassato) }) : ""} />
      </section>

      {/* Punto di pareggio (spostato dagli Incassi) */}
      {breakEven && breakEven.break_even > 0 && (
        <section className="sp-card p-6" data-testid="dashboard-breakeven">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="font-display text-xl font-black inline-flex items-center gap-2">
                <Target className="h-5 w-5 text-amber-500" /> {t("dashboard.breakEven.title")}
              </h2>
              <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">
                <Trans i18nKey="dashboard.breakEven.description" components={[<Link to="/app/costs" className="font-bold text-[color:var(--sp-brand)]" key="0" />]} />
              </p>
            </div>
            <div className="text-right">
              <div className="label-eyebrow text-[10px]">{t("dashboard.breakEven.forPeriod")}</div>
              <div className="font-display text-3xl font-black text-amber-600 tabular-nums">{eur(breakEven.break_even)}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <BreakEvenTile label={t("dashboard.kpi.revenue")} value={eur(breakEven.revenue_total)} tint="brand" />
            <BreakEvenTile label={t("dashboard.breakEven.fixedCosts")} value={eur(breakEven.fixed_costs)} hint={t("dashboard.breakEven.fixedCostsHint")} />
            <BreakEvenTile label={t("dashboard.breakEven.variableCosts")} value={eur(breakEven.variable_costs_period)} hint={t("dashboard.breakEven.variableCostsHint")} />
            <BreakEvenTile label={t("dashboard.metrics.costPerMinute")}
              value={breakEven.costo_al_minuto > 0 ? `€ ${breakEven.costo_al_minuto.toFixed(3)}` : "—"}
              hint={breakEven.total_open_minutes ? t("dashboard.breakEven.openHours", { n: Math.round(breakEven.total_open_minutes / 60) }) : t("dashboard.breakEven.setHours")} tint="rose" />
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-white border border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-eyebrow">{t("dashboard.breakEven.coverage")}</div>
                <div className="text-2xl font-display font-black mt-1 inline-flex items-center gap-2">
                  {(breakEven.break_even_coverage || 0).toFixed(0)}%
                  {breakEven.break_even_gap >= 0 ? (
                    <span className="text-xs font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">+ {eur(breakEven.break_even_gap)} {t("dashboard.breakEven.margin")}</span>
                  ) : (
                    <span className="text-xs font-bold text-rose-600 bg-rose-100 px-2 py-0.5 rounded-full">− {eur(Math.abs(breakEven.break_even_gap))} {t("dashboard.breakEven.belowTarget")}</span>
                  )}
                </div>
              </div>
              <div className="text-right text-xs text-[color:var(--sp-text-muted)]">
                {t("dashboard.kpi.revenue")} {eur(breakEven.revenue_total)}<br />
                {t("dashboard.breakEven.onBreakEven")} {eur(breakEven.break_even)}
              </div>
            </div>
            <div className="h-3 rounded-full bg-white mt-3 overflow-hidden relative">
              <div className="h-full transition-all"
                   style={{
                     width: `${Math.min(100, breakEven.break_even_coverage || 0)}%`,
                     background: breakEven.break_even_coverage >= 100 ? "#10B981" : "#F59E0B",
                   }} />
              {breakEven.break_even_coverage > 100 && (
                <div className="absolute right-0 top-0 h-full text-[10px] font-black text-white flex items-center pr-2">
                  PAREGGIO
                </div>
              )}
            </div>
            <p className="text-[10px] text-[color:var(--sp-text-muted)] mt-2 inline-flex items-center gap-1">
              <Info className="h-3 w-3" /> <Trans i18nKey="dashboard.breakEven.footnote" components={[<Link to="/app/costs" className="font-bold text-[color:var(--sp-brand)]" key="0" />]} />
            </p>
          </div>
        </section>
      )}

      {/* Percorsi in scadenza — evidenzia i clienti con 1/2/3 sedute rimaste */}
      {packagesLow.length > 0 && (
        <section className="sp-card p-6" data-testid="packages-low-card">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="font-display text-xl font-black inline-flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Percorsi in scadenza
              </h2>
              <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">
                {packagesLow.length} client{packagesLow.length === 1 ? "e" : "i"} con 1–3 sedute rimaste. Proponi subito il rinnovo per non perdere il ricavo ricorrente.
              </p>
            </div>
            <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800 whitespace-nowrap">
              {packagesLow.length} totali
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="packages-low-list">
            {packagesLow.slice(0, 9).map((row) => (
              <Link
                key={row.client_package_id}
                to={`/app/clients/${row.client_id}`}
                data-testid={`packages-low-row-${row.client_package_id}`}
                className="rounded-xl border border-[color:var(--sp-border)] hover:border-amber-400 hover:shadow-md transition-all p-3 flex items-center gap-3 group"
              >
                <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                     style={{ background: row.package_color || "#EC4899" }}>
                  <Package className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate group-hover:text-[color:var(--sp-brand)]">{row.client_name}</div>
                  <div className="text-[11px] text-[color:var(--sp-text-muted)] truncate">{row.package_name}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className={`text-lg font-black leading-none ${row.remaining === 1 ? "text-red-600" : row.remaining === 2 ? "text-amber-600" : "text-yellow-600"}`}>
                    {row.remaining}
                  </div>
                  <div className="text-[9px] uppercase tracking-wider text-[color:var(--sp-text-muted)]">
                    {row.remaining === 1 ? "seduta" : "sedute"}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {packagesLow.length > 9 && (
            <div className="mt-3 text-xs text-center text-[color:var(--sp-text-muted)]">
              +{packagesLow.length - 9} altri percorsi in scadenza
            </div>
          )}
        </section>
      )}

      {/* Expected revenue */}
      {expected && (
        <section className="sp-card p-6" data-testid="expected-revenue">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display text-xl font-black inline-flex items-center gap-2"><Clock className="h-5 w-5 text-[color:var(--sp-brand)]" /> Incassi attesi</h2>
              <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">
                Somma dei prezzi servizi degli appuntamenti pianificati da domani in poi, nel periodo selezionato ({fmtItDate(expected.date_from)} → {fmtItDate(expected.date_to)}).
              </p>
            </div>
            <div className="text-right">
              <div className="font-display text-4xl font-black text-[color:var(--sp-brand)]" data-testid="expected-total">{eur(expected.total)}</div>
              <div className="text-xs text-[color:var(--sp-text-muted)]">{expected.count} appuntamenti pianificati</div>
            </div>
          </div>
        </section>
      )}

      {/* Trend chart — grouped bars with Y-axis, optional costs */}
      <section className="sp-card p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-xl font-black">Andamento incassi</h2>
            <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">
              {trend.length > 0 && <>Incassi periodo: <strong className="text-[color:var(--sp-brand)]">{eur(revenueSum)}</strong></>}
              {showCosts && trend.length > 0 && <> · Costi: <strong className="text-[color:var(--sp-danger)]">{eur(costsSum)}</strong> · Margine: <strong className={(revenueSum - costsSum) >= 0 ? "text-[color:var(--sp-success)]" : "text-[color:var(--sp-danger)]"}>{eur(revenueSum - costsSum)}</strong></>}
              {bestMonth && bestMonth.revenue > 0 && (() => { const [y, m] = bestMonth.month.split("-"); return <> · Miglior mese: {MONTHS_IT[Number(m) - 1]} {y}</>; })()}
            </p>
          </div>
          <div className="inline-flex items-center gap-2 flex-wrap">
            <button
              data-testid="trend-toggle-costs"
              onClick={() => setShowCosts((v) => !v)}
              className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${showCosts ? "bg-[color:var(--sp-danger)] text-white border-[color:var(--sp-danger)]" : "bg-white text-[color:var(--sp-text-secondary)] border-[color:var(--sp-border)]"}`}
            >
              {showCosts ? "Nascondi costi" : "Mostra costi"}
            </button>
            <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1">
              {TREND_OPTIONS.map((opt) => (
                <button key={opt.key} data-testid={`trend-${opt.key}`} onClick={() => setTrendKey(opt.key)}
                        className={`px-3 py-1 rounded-full text-xs font-bold ${trendKey === opt.key ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {trend.length === 0 ? (
          <div className="h-40 flex items-center justify-center text-sm text-[color:var(--sp-text-muted)]">Caricamento…</div>
        ) : (
          <TrendBarChart trend={trend} max={maxTrend} showCosts={showCosts} />
        )}
      </section>

      {/* Period comparator */}
      <section className="sp-card p-6" data-testid="period-comparator">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-xl font-black inline-flex items-center gap-2"><ArrowRightLeft className="h-5 w-5 text-[color:var(--sp-brand)]" /> Confronta due periodi</h2>
            <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">Scegli il preset o imposta un intervallo personalizzato.</p>
          </div>
          <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1" data-testid="compare-preset">
            {[
              { key: "yoy", label: t("dashboard.compare.yoy") },
              { key: "mom", label: t("dashboard.compare.mom") },
              { key: "custom", label: t("dashboard.presets.custom") },
            ].map((p) => (
              <button
                key={p.key}
                data-testid={`compare-preset-${p.key}`}
                onClick={() => {
                  setComparePreset(p.key);
                  if (p.key === "yoy") {
                    setPeriodA({ from: firstOfMonth(today), to: lastOfMonth(today) });
                    setPeriodB({ from: firstOfMonth(sameMonthLastYear(today)), to: lastOfMonth(sameMonthLastYear(today)) });
                  } else if (p.key === "mom") {
                    const d = new Date(today + "T00:00:00Z");
                    d.setUTCMonth(d.getUTCMonth() - 1);
                    const prev = ymd(d);
                    setPeriodA({ from: firstOfMonth(today), to: lastOfMonth(today) });
                    setPeriodB({ from: firstOfMonth(prev), to: lastOfMonth(prev) });
                  }
                }}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${comparePreset === p.key ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}
              >
                {t(p.label)}
              </button>
            ))}
          </div>
        </div>

        {/* Hero delta cards — la parte che l'utente guarda per prima */}
        {dataA && dataB && (
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="compare-deltas">
            <DeltaCard label="Incassi" a={dataA.revenue} b={dataB.revenue} format="eur" />
            <DeltaCard label="Costi" a={dataA.costs} b={dataB.costs} format="eur" kind="costs" />
            <DeltaCard label="Margine" a={dataA.margin} b={dataB.margin} format="eur" />
            <DeltaCard label="Appuntamenti" a={dataA.appointments} b={dataB.appointments} format="int" />
          </div>
        )}

        {/* Compact period rows — dettaglio dei due periodi come tabella leggera */}
        <div className="mt-5 space-y-2">
          <PeriodRow tone="brand" period={periodA} setPeriod={setPeriodA} data={dataA} label={t("dashboard.compare.periodA")} editable={comparePreset === "custom"} />
          <PeriodRow tone="muted" period={periodB} setPeriod={setPeriodB} data={dataB} label={t("dashboard.compare.periodB")} editable={comparePreset === "custom"} />
        </div>
      </section>

      {/* Composition of costs + Top services */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="sp-card p-6">
          <div>
            <h2 className="font-display text-xl font-black inline-flex items-center gap-2"><PieChart className="h-5 w-5 text-[color:var(--sp-brand)]" /> Composizione costi</h2>
            <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">{rangeLabel} · {t("dashboard.whereMoneyGoes")}</p>
          </div>
          {costsPie.length === 0 ? (
            <div className="h-52 flex items-center justify-center text-sm text-[color:var(--sp-text-muted)]">
              Nessun costo registrato nel periodo. Aggiungi voci in <strong className="text-[color:var(--sp-text)] ml-1">Costi</strong>.
            </div>
          ) : (
            <div className="mt-6 flex items-center gap-6 flex-wrap">
              <DonutChart data={costsPie.map((c) => ({ value: c.value, color: c.color }))} size={200} thickness={32}
                centerLabel="Totale" centerValue={eur(costsTotal)} />
              <div className="flex-1 min-w-[180px] space-y-2">
                {costsPie.sort((a, b) => b.value - a.value).map((c) => {
                  const pct = (c.value / costsTotal) * 100;
                  return (
                    <div key={c.key}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                          <span className="font-bold">{c.label}</span>
                        </span>
                        <span><strong>{eur(c.value)}</strong> <span className="text-[color:var(--sp-text-muted)] ml-1">{pct.toFixed(0)}%</span></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="sp-card p-6">
          <h2 className="font-display text-xl font-black inline-flex items-center gap-2"><Sparkles className="h-5 w-5 text-[color:var(--sp-brand)]" /> Servizi top</h2>
          {topServices.length === 0 ? (
            <div className="mt-4 text-sm text-[color:var(--sp-text-muted)]">Non abbastanza dati per calcolare i top servizi.</div>
          ) : (
            <div className="mt-4 space-y-3">
              {topServices.map((s) => {
                const maxR = Math.max(1, ...topServices.map((x) => x.revenue));
                const pct = (s.revenue / maxR) * 100;
                return (
                  <div key={s.service_id}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                        <span className="font-bold">{s.name}</span>
                      </span>
                      <span className="text-xs text-[color:var(--sp-text-secondary)]"><strong>{eur(s.revenue)}</strong> · {s.count}x</span>
                    </div>
                    <div className="h-2 rounded-full bg-[color:var(--sp-subtle)] mt-1 overflow-hidden">
                      <div style={{ width: `${pct}%`, background: s.color }} className="h-full" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function niceCeil(v) {
  // Round a max value up to a "nice" number for Y-axis
  if (v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const n = v / base;
  let nice;
  if (n <= 1) nice = 1;
  else if (n <= 2) nice = 2;
  else if (n <= 2.5) nice = 2.5;
  else if (n <= 5) nice = 5;
  else nice = 10;
  return nice * base;
}
function formatEurAxis(n) {
  if (n >= 1000) return `€${(n / 1000).toLocaleString(locale(), { maximumFractionDigits: 1 })}k`;
  return `€${Math.round(n)}`;
}

function TrendBarChart({ trend, max, showCosts }) {
  const width = 720;
  const height = 240;
  const padding = { top: 16, right: 20, bottom: 40, left: 56 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const n = trend.length;
  if (n === 0) return null;

  const yMax = niceCeil(max);
  const yTicks = 4; // 5 lines including 0 and max
  const ticks = Array.from({ length: yTicks + 1 }, (_, i) => (yMax * i) / yTicks);

  const groupW = innerW / n;
  const barPadRatio = 0.22;                    // gap on each side of the group
  const usableW = groupW * (1 - barPadRatio * 2);
  const barW = showCosts ? usableW / 2 - 2 : usableW * 0.7;

  return (
    <div className="mt-6 overflow-x-auto" data-testid="trend-chart">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="bar-rev" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--sp-brand)" stopOpacity="1" />
            <stop offset="100%" stopColor="var(--sp-brand)" stopOpacity="0.55" />
          </linearGradient>
          <linearGradient id="bar-cost" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--sp-danger)" stopOpacity="1" />
            <stop offset="100%" stopColor="var(--sp-danger)" stopOpacity="0.55" />
          </linearGradient>
        </defs>

        {/* Y grid + Y labels */}
        {ticks.map((v, i) => {
          const y = padding.top + innerH - (v / yMax) * innerH;
          return (
            <g key={i}>
              <line x1={padding.left} x2={padding.left + innerW} y1={y} y2={y}
                    stroke="var(--sp-border)" strokeDasharray={i === 0 ? "0" : "3 3"} strokeWidth={i === 0 ? 1 : 0.5} />
              <text x={padding.left - 8} y={y + 3} textAnchor="end"
                    className="fill-[color:var(--sp-text-muted)]" style={{ fontSize: 10, fontWeight: 700 }}>
                {formatEurAxis(v)}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {trend.map((t, i) => {
          const gx = padding.left + groupW * i + groupW * barPadRatio;
          const revH = ((t.revenue || 0) / yMax) * innerH;
          const costH = ((t.costs || 0) / yMax) * innerH;
          const baseY = padding.top + innerH;
          if (showCosts) {
            return (
              <g key={i}>
                <rect x={gx} y={baseY - revH} width={barW} height={Math.max(0, revH)} rx={3}
                      fill="url(#bar-rev)">
                  <title>{t.month} · Incassi € {t.revenue}</title>
                </rect>
                <rect x={gx + barW + 4} y={baseY - costH} width={barW} height={Math.max(0, costH)} rx={3}
                      fill="url(#bar-cost)">
                  <title>{t.month} · Costi € {t.costs || 0}</title>
                </rect>
                <text x={padding.left + groupW * (i + 0.5)} y={height - 22} textAnchor="middle"
                      className="fill-[color:var(--sp-text-secondary)]" style={{ fontSize: 10, fontWeight: 700 }}>
                  {(() => { const [y, m] = t.month.split("-"); return `${MONTHS_IT[Number(m) - 1]} ${y.slice(2)}`; })()}
                </text>
                {revH > 20 && (
                  <text x={gx + barW / 2} y={baseY - revH - 4} textAnchor="middle"
                        className="fill-[color:var(--sp-brand)]" style={{ fontSize: 9, fontWeight: 800 }}>
                    {formatEurAxis(t.revenue)}
                  </text>
                )}
                {costH > 20 && (
                  <text x={gx + barW + 4 + barW / 2} y={baseY - costH - 4} textAnchor="middle"
                        className="fill-[color:var(--sp-danger)]" style={{ fontSize: 9, fontWeight: 800 }}>
                    {formatEurAxis(t.costs || 0)}
                  </text>
                )}
              </g>
            );
          }
          // Single-series (revenue only) — center the bar in the group
          const cx = padding.left + groupW * (i + 0.5) - barW / 2;
          return (
            <g key={i}>
              <rect x={cx} y={baseY - revH} width={barW} height={Math.max(0, revH)} rx={3}
                    fill="url(#bar-rev)">
                <title>{t.month} · Incassi € {t.revenue}</title>
              </rect>
              <text x={padding.left + groupW * (i + 0.5)} y={height - 22} textAnchor="middle"
                    className="fill-[color:var(--sp-text-secondary)]" style={{ fontSize: 10, fontWeight: 700 }}>
                {(() => { const [y, m] = t.month.split("-"); return `${MONTHS_IT[Number(m) - 1]} ${y.slice(2)}`; })()}
              </text>
              {revH > 20 && (
                <text x={padding.left + groupW * (i + 0.5)} y={baseY - revH - 4} textAnchor="middle"
                      className="fill-[color:var(--sp-brand)]" style={{ fontSize: 9, fontWeight: 800 }}>
                  {formatEurAxis(t.revenue)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="mt-2 flex items-center gap-4 text-xs">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "var(--sp-brand)" }} /> Incassi</span>
        {showCosts && (
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "var(--sp-danger)" }} /> Costi</span>
        )}
      </div>
    </div>
  );
}

function PeriodRow({ label, period, setPeriod, data, tone = "brand", editable = false }) {
  const dot = tone === "brand" ? "var(--sp-brand)" : "#94A3B8";
  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3 items-center p-3 rounded-lg border border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)]/30">
      {/* Label + date range */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ background: dot }} />
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)] truncate">{label}</div>
          {editable ? (
            <div className="inline-flex items-center gap-1.5 mt-0.5">
              <input type="date" value={period.from} onChange={(e) => setPeriod({ ...period, from: e.target.value })} className="sp-input !py-0.5 !px-1.5 text-[11px] w-auto" />
              <span className="text-[10px] text-[color:var(--sp-text-muted)]">→</span>
              <input type="date" value={period.to} onChange={(e) => setPeriod({ ...period, to: e.target.value })} className="sp-input !py-0.5 !px-1.5 text-[11px] w-auto" />
            </div>
          ) : (
            <div className="text-xs font-semibold text-[color:var(--sp-text-secondary)] mt-0.5 truncate">
              {fmtItDate(period.from)} → {fmtItDate(period.to)}
            </div>
          )}
        </div>
      </div>
      {/* Metrics inline */}
      <div className="grid grid-cols-4 gap-2 text-sm">
        <PeriodMetric label="Incassi" value={eur(data?.revenue)} tone="brand" />
        <PeriodMetric label="Costi" value={eur(data?.costs)} tone="danger" />
        <PeriodMetric label="Margine" value={eur(data?.margin)} tone={(data?.margin ?? 0) >= 0 ? "success" : "danger"} />
        <PeriodMetric label="Appt." value={(data?.appointments ?? 0).toLocaleString(locale())} />
      </div>
    </div>
  );
}

function PeriodMetric({ label, value, tone }) {
  const color = tone === "brand" ? "var(--sp-brand)"
              : tone === "danger" ? "var(--sp-danger)"
              : tone === "success" ? "var(--sp-success)"
              : "var(--sp-text)";
  return (
    <div className="min-w-0">
      <div className="label-eyebrow text-[9px]">{label}</div>
      <div className="font-display font-black text-sm md:text-base tabular-nums truncate" style={{ color }}>{value}</div>
    </div>
  );
}

function DeltaCard({ label, a, b, format, kind = "revenue" }) {
  const diff = a - b;
  const pct = b ? (diff / b) * 100 : (a > 0 ? 100 : 0);
  const positive = kind === "costs" ? diff < 0 : diff > 0;
  const zero = diff === 0;
  const color = zero ? "var(--sp-text-muted)" : (positive ? "var(--sp-success)" : "var(--sp-danger)");
  const fmt = (v) => format === "eur" ? eur(v) : (v ?? 0).toLocaleString(locale());
  const signed = (v) => (v > 0 ? "+" : "") + fmt(v);
  const Icon = zero ? ArrowRightLeft : (positive ? ArrowUpRight : ArrowDownRight);
  return (
    <div className="p-3 rounded-lg border border-[color:var(--sp-border)] bg-white">
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className="mt-1 flex items-baseline justify-between gap-1">
        <span className="font-display font-black text-lg" style={{ color }}>{signed(diff)}</span>
        <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color }}>
          <Icon className="h-3 w-3" />{pct > 0 ? "+" : ""}{pct.toFixed(1)}%
        </span>
      </div>
      <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-0.5">{fmt(a)} vs {fmt(b)}</div>
    </div>
  );
}

function BreakEvenTile({ label, value, hint, tint }) {
  const tintMap = { brand: "text-[color:var(--sp-brand)]", rose: "text-rose-700" };
  const tc = tintMap[tint] || "text-[color:var(--sp-text)]";
  return (
    <div className="p-3 rounded-lg border border-[color:var(--sp-border)] bg-white">
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className={`font-display font-black text-lg mt-1 tabular-nums ${tc}`}>{value}</div>
      {hint && <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-0.5">{hint}</div>}
    </div>
  );
}